import type {
  OutputFormat,
  ProResProfile,
  RenderSettings,
} from "@/types/render";

interface RenderControlsProps {
  settings: RenderSettings;
  onChange: (next: RenderSettings) => void;
  onClose?: () => void;
}

interface FormatInfo {
  value: OutputFormat;
  label: string;
  description: string;
  badge: string;
}

const FORMATS: readonly FormatInfo[] = [
  {
    value: "mp4",
    label: "MP4 (H.264)",
    description: "Universal compatibility. Best for web, social, and previews.",
    badge: "No transparency",
  },
  {
    value: "mp4-matte",
    label: "MP4 + Matte",
    description:
      "Two small H.264 files: the graphic over black plus a luma track matte. Composite in your NLE with Track Matte Key for transparency without ProRes file sizes.",
    badge: "Transparency via matte • Small files",
  },
  {
    value: "webm",
    label: "WebM (VP9)",
    description: "Modern web codec with alpha channel support.",
    badge: "Supports alpha",
  },
  {
    value: "prores",
    label: "ProRes (QuickTime)",
    description:
      "Professional editing codec. Alpha is STRAIGHT (unmatted) — set your NLE's interpretation to match.",
    badge: "Straight alpha • Large file",
  },
  {
    value: "gif",
    label: "GIF",
    description: "Animated image. Easy to share, no audio.",
    badge: "Small file • 1-bit alpha",
  },
];

interface ProResProfileInfo {
  value: ProResProfile;
  label: string;
  description: string;
}

const PRORES_PROFILES: readonly ProResProfileInfo[] = [
  { value: "4444-xq", label: "4444 XQ", description: "Highest quality, with alpha" },
  { value: "4444", label: "4444", description: "High quality, with alpha" },
  { value: "hq", label: "HQ", description: "High quality, no alpha" },
  { value: "standard", label: "Standard", description: "Standard quality, no alpha" },
  { value: "light", label: "Light", description: "Lower bitrate" },
  { value: "proxy", label: "Proxy", description: "Smallest file, offline editing" },
];

/** True when the chosen format pipes through an x264/vp9-style CRF knob. */
function formatSupportsCrf(format: OutputFormat): boolean {
  // mp4-matte's CRF applies to the H.264 fill (the matte is always encoded
  // near-lossless).
  return (
    format === "mp4" || format === "webm" || format === "h265" || format === "mp4-matte"
  );
}

export function RenderControls({ settings, onChange, onClose }: RenderControlsProps) {
  function update(patch: Partial<RenderSettings>) {
    onChange({ ...settings, ...patch });
  }

  function setFormat(format: OutputFormat) {
    const patch: Partial<RenderSettings> = { format };
    // Initialise a sensible ProRes profile when switching into ProRes.
    if (format === "prores" && !settings.prores_profile) {
      patch.prores_profile = "hq";
    }
    if (format !== "prores") {
      patch.prores_profile = undefined;
    }
    update(patch);
  }

  const durationSeconds = settings.duration_frames / Math.max(settings.fps, 1);

  // Popup mode (onClose present, floated by RenderButton) wears card chrome
  // and its own heading; inline mode (Settings tab) is bare — the tab already
  // provides the container and the "Render output" section label.
  const isPopup = !!onClose;
  return (
    <div
      className={
        isPopup
          ? "flex w-[420px] flex-col gap-5 rounded-xl border border-line bg-surface-2 p-4 text-xs text-ink shadow-lg"
          : "flex w-full max-w-[420px] flex-col gap-5 text-xs text-ink"
      }
    >
      {isPopup && (
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink">Render Settings</h3>
          <button
            onClick={onClose}
            className="text-ink-3 transition-colors hover:text-ink"
            aria-label="Close render settings"
          >
            ✕
          </button>
        </div>
      )}

      {/* Format */}
      <section className="flex flex-col gap-2">
        <label className="text-xs font-medium text-ink-2">
          Format
        </label>
        <div className="grid grid-cols-2 gap-2">
          {FORMATS.map((f) => {
            const active = settings.format === f.value;
            return (
              <button
                key={f.value}
                onClick={() => setFormat(f.value)}
                className={`flex flex-col items-start gap-1 rounded-lg border px-3 py-2 text-left transition-colors ${
                  active
                    ? "border-accent bg-accent-soft text-ink"
                    : "border-line bg-surface hover:bg-surface-3"
                }`}
              >
                <span className="text-xs font-semibold">{f.label}</span>
                <span className="text-[10px] text-ink-3">{f.description}</span>
                <span
                  className={`mt-1 rounded px-1.5 py-0.5 text-[9px] ${
                    active
                      ? "bg-accent-soft text-accent"
                      : "bg-surface-3 text-ink-2"
                  }`}
                >
                  {f.badge}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* ProRes profile */}
      {settings.format === "prores" && (
        <section className="flex flex-col gap-2">
          <label className="text-xs font-medium text-ink-2">
            ProRes Profile
          </label>
          <select
            value={settings.prores_profile ?? "hq"}
            onChange={(e) => update({ prores_profile: e.target.value as ProResProfile })}
            className="rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-ink focus:border-line-strong focus:outline-none"
          >
            {PRORES_PROFILES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label} — {p.description}
              </option>
            ))}
          </select>
          {(settings.prores_profile === "4444" ||
            settings.prores_profile === "4444-xq") && (
            <label className="flex items-start gap-1.5 text-[10px] font-medium text-ink-2">
              <input
                type="checkbox"
                checked={settings.dither !== false}
                onChange={(e) => update({ dither: e.target.checked })}
                className="mt-0.5 h-3.5 w-3.5 accent-accent"
              />
              <span>
                Anti-banding dither
                <span className="block font-normal text-ink-3">
                  Hides 8-bit contour rings in soft glows with imperceptible grain
                </span>
              </span>
            </label>
          )}
        </section>
      )}

      {/* Resolution — render OUTPUT size (the template's native canvas lives
          in the Settings tab's Canvas section; this scales the render, e.g.
          a 1080p canvas rendered at 4K). */}
      <section className="flex flex-col gap-2">
        <label className="text-xs font-medium text-ink-2">Resolution</label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={16}
            max={8192}
            value={settings.width}
            onChange={(e) => update({ width: Math.max(16, Math.min(8192, parseInt(e.target.value, 10) || 16)) })}
            className="w-20 rounded-lg border border-line bg-surface px-2 py-1 text-xs text-ink focus:border-line-strong focus:outline-none"
            aria-label="Render width"
          />
          <span className="text-[10px] text-ink-3">×</span>
          <input
            type="number"
            min={16}
            max={8192}
            value={settings.height}
            onChange={(e) => update({ height: Math.max(16, Math.min(8192, parseInt(e.target.value, 10) || 16)) })}
            className="w-20 rounded-lg border border-line bg-surface px-2 py-1 text-xs text-ink focus:border-line-strong focus:outline-none"
            aria-label="Render height"
          />
          <span className="text-[10px] text-ink-3">px</span>
        </div>
        <div className="flex gap-1.5">
          {[
            { label: "1080p", w: 1920, h: 1080 },
            { label: "1440p", w: 2560, h: 1440 },
            { label: "4K", w: 3840, h: 2160 },
            { label: "Vertical", w: 1080, h: 1920 },
          ].map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => update({ width: p.w, height: p.h })}
              className={`rounded-md px-2 py-0.5 text-[10px] font-medium transition-colors ${
                settings.width === p.w && settings.height === p.h
                  ? "bg-accent-soft text-accent"
                  : "bg-surface-3 text-ink-2 hover:text-ink"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </section>

      {/* Duration — Auto follows the template's timing fields (a 5-minute
          countdown renders 5 minutes); editing the seconds switches to a
          manual override. */}
      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-ink-2">
            Duration
          </label>
          <label className="flex items-center gap-1.5 text-[10px] font-medium text-ink-2">
            <input
              type="checkbox"
              checked={settings.duration_auto !== false}
              onChange={(e) => update({ duration_auto: e.target.checked })}
              className="h-3.5 w-3.5 accent-accent"
            />
            Auto (follow fields)
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0.1}
            step={0.1}
            value={Number(durationSeconds.toFixed(2))}
            disabled={settings.duration_auto !== false}
            onChange={(e) => {
              const seconds = parseFloat(e.target.value) || 0;
              update({
                duration_frames: Math.max(1, Math.round(seconds * settings.fps)),
                duration_auto: false,
              });
            }}
            className="w-24 rounded-lg border border-line bg-surface px-2 py-1 text-xs text-ink focus:border-line-strong focus:outline-none disabled:opacity-50"
          />
          <span className="text-[10px] text-ink-3">seconds</span>
        </div>
        <span className="text-[10px] text-ink-3">
          {settings.duration_frames} frames at {settings.fps}fps
          {settings.duration_auto !== false && " — from timing fields"}
        </span>
      </section>

      {/* Quality (CRF) */}
      {formatSupportsCrf(settings.format) && (
        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-ink-2">
              Quality (CRF)
            </label>
            <span className="text-[10px] text-ink-2">
              {settings.crf ?? 23}{" "}
              <span className="text-ink-3">
                {(settings.crf ?? 23) === 0
                  ? "lossless"
                  : (settings.crf ?? 23) <= 18
                    ? "visually lossless"
                    : (settings.crf ?? 23) <= 23
                      ? "high"
                      : (settings.crf ?? 23) <= 28
                        ? "medium"
                        : "low"}
              </span>
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={51}
            value={settings.crf ?? 23}
            onChange={(e) => update({ crf: parseInt(e.target.value, 10) })}
            className="w-full accent-accent"
          />
          <div className="flex justify-between text-[9px] text-ink-3">
            <span>0 (lossless)</span>
            <span>23 (default)</span>
            <span>51 (worst)</span>
          </div>
        </section>
      )}
    </div>
  );
}
